import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { projects } from '../mockData';
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_SEO,
  PROJECTS_VISIBLE,
  SITE_NAME,
  getCanonicalUrl,
  getOrganizationSchema,
  getProjectSchema,
  getSeoForPath,
} from '../lib/seo';

const JSON_LD_ID = 'luxdall-json-ld';

function upsertMeta(attribute, key, content) {
  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function upsertJsonLd(schemas) {
  let element = document.getElementById(JSON_LD_ID);
  if (!element) {
    element = document.createElement('script');
    element.id = JSON_LD_ID;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
}

export function SeoManager() {
  const { pathname } = useLocation();
  const seo = getSeoForPath(pathname);
  const title = seo.title ?? DEFAULT_SEO.title;
  const description = seo.description ?? DEFAULT_SEO.description;
  const canonical = getCanonicalUrl(pathname);
  const image = seo.image ?? DEFAULT_OG_IMAGE;
  const noindex = Boolean(seo.noindex);
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';

  useEffect(() => {
    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', robots);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:locale', 'pt_PT');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
    upsertLink('canonical', canonical);

    const schemas = [getOrganizationSchema()];
    if (PROJECTS_VISIBLE) {
      const projectMatch = pathname.match(/^\/projetos\/(\d+)$/);
      if (projectMatch) {
        const project = projects.find((p) => p.id === parseInt(projectMatch[1], 10));
        if (project) {
          schemas.push(getProjectSchema(project));
        }
      }
    }
    upsertJsonLd(schemas);
  }, [title, description, canonical, image, robots, pathname]);

  return null;
}
